import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import {
  useBusinessStore,
  useDirectBackend,
  usePinia,
  type IDomainItem,
  type IDomainMoney,
} from "#imports";

// Define the state interface
type IState = IDomainItem & {
  isLoaded: boolean;
  isProcessing: boolean;
};

// Helper function to create the initial state
function createInitialState(currency: string): IState {
  const zeroPrice: IDomainMoney = {
    currency,
    value: 0,
  };

  return {
    isLoaded: false,
    isProcessing: false,
    id: "",
    plu: "",
    name: "",
    type: "product",
    price: zeroPrice,
    bottleDepositPrice: zeroPrice,
    allergens: [],
    consumables: [],
    calories: 0,
    caloriesInKj: 0,
    caloriesRangeHigh: 0,
    defaultQuantity: 0,
    isCombo: false,
    inStock: false,
    modifierLists: [],
    description: undefined,
    originalPrice: undefined,
    photo: undefined,
    nutritional: undefined,
    beverage: undefined,
    packaging: undefined,
    supplemental: undefined,
    menuId: undefined,
  };
}

export const useItemStore = defineStore("item", () => {
  const pinia = usePinia();
  const businessStore = useBusinessStore(pinia);

  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState(businessStore.currency));

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState(businessStore.currency));
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const { menuRepo } = useDirectBackend();
  async function load(id: string, outletId: string) {
    if (state.isLoaded && state.id === id) {
      // item is already loaded
      return;
    }

    reset();
    state.isProcessing = true;

    try {
      const data = await menuRepo.getItem({ itemId: id, outletId });

      patch({
        isLoaded: true,
        ...data,
      });
    } finally {
      patch({
        isProcessing: false,
      });
    }
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    load,
  };
});
